import { hasValue } from './globals';

/**
 * Options for formatting currency strings
 */
export type FormatCurrencyOptions = {
  /**
   * If true, shows cents in the formatted result instead of truncating to dollars
   * @default false
   */
  showCents?: boolean;

  /**
   * If true, strips the currency sign prefix
   * @default false
   */
  stripSigil?: boolean;

  /**
   * Fallback string to return if value is not present
   * @default '-'
   */
  fallback?: string;

  /**
   * If true, treats the input as a dollar amount. Otherwise, it is treated as cents
   * @default false
   */
  inDollars?: boolean;

  /**
   * If true, inverts the sign of the value if non-null (before any formatting concerns)
   * @default false
   */
  negate?: boolean;
};

const defaultFormatCurrencyOptions: Required<FormatCurrencyOptions> = {
  showCents: false,
  stripSigil: false,
  fallback: '-',
  inDollars: false,
  negate: false,
};

const dollarsFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  roundingMode: 'trunc',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const centsFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  roundingMode: 'trunc',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats a number representing currency as a string
 *
 * By default this function:
 *  - assumes the input is in cents
 *  - truncates cents from the resulting dollar amount
 *  - adds a currency sign prefix
 *  - falls back to the string '-' if the input value is null or undefined
 *
 * @param value - the number to format
 * @param options - {@link FormatCurrencyOptions | formatting options}
 * @returns the formatted currency string
 */
export const formatCurrency = (
  value: number | null,
  options?: Readonly<FormatCurrencyOptions>,
): string => {
  const resolvedOptions = {
    ...defaultFormatCurrencyOptions,
    ...options,
  };

  if (!hasValue(value)) {
    return resolvedOptions.fallback;
  }

  let input = value;

  if (resolvedOptions.negate) {
    input = -input;
  }

  if (Object.is(input, -0)) {
    input = 0;
  }

  if (!resolvedOptions.inDollars) {
    input = input / 100;
  }

  let output = (resolvedOptions.showCents ? centsFormatter : dollarsFormatter).format(input);

  if (resolvedOptions.stripSigil) {
    output = output.replace('$', '');
  }

  return output;
};

/**
 * Rounds a cent value to the nearest thousand dollars (100,000 cents)
 *
 * @param cents - the number of cents to round
 * @returns the rounded cent value
 */
export const roundToNearestThousandCents = (cents: number): number =>
  Math.sign(cents) * Math.round(Math.abs(cents) / 100_000) * 100_000;

/**
 * Formats a currency value in cents as an abbreviated string rounded to the nearest thousand
 * e.g. 49500000 cents → "$495K"
 *
 * @param value - the number to format, in cents
 * @returns the abbreviated currency string, or '-' if null
 */
export const formatCurrencyCompact = (value: number | null): string => {
  if (!hasValue(value)) return '-';
  const thousands = Math.round(value / 100_000);
  if (thousands >= 1000) {
    return `$${(value / 100_000_000).toFixed(2)}M`;
  }
  return `$${thousands}K`;
};

/**
 * Formats a number into a percent string
 *
 * @param percentage - The number to format
 * @param minimumFractionDigits - The minimum number of digits to display after the decimal point
 * @param maximumFractionDigits - The maximum number of digits to display after the decimal point
 * @returns The formatted string
 * @public
 */
export const formatPercent = (
  percentage: number,
  minimumFractionDigits?: number,
  maximumFractionDigits?: number,
) =>
  new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: minimumFractionDigits ?? 1,
    maximumFractionDigits,
  }).format(percentage);

/**
 * Takes in phone number format +1XXXXXXXXXX and transforms it into XXX-XXX-XXXX
 *
 * @param rawPhoneNumber - The phone number to format
 * @returns The formatted phone number
 */
export const formatPhoneNumber = (rawPhoneNumber: string | null | undefined): string => {
  const cleaned = ('' + rawPhoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[2]}) ${match[3]}-${match[4]}`;
  }
  return '';
};

export const negate = (value: number | undefined | null): number | null => {
  if (hasValue(value)) {
    return -value;
  }

  return null;
};

export const absolute = (value: number | undefined | null): number | null => {
  if (hasValue(value)) {
    return Math.abs(value);
  }

  return null;
};

/**
 * Converts cents to dollars
 *
 * @param cents - The number of cents
 * @returns The number of dollars
 */
export function centsToDollars(cents: undefined | null): null;
export function centsToDollars(cents: number): number;
export function centsToDollars(cents: number | undefined | null): number | null;
export function centsToDollars(cents: number | undefined | null): number | null {
  return hasValue(cents) ? Math.floor(cents / 100) : null;
}

/**
 *  Converts a snake case string to words
 *
 *  @returns The formatted string: "HELLO_HOW_ARE_YOU" -> "Hello How Are You"
 *  @param str - The string to format
 */
export const capitalizedSnakeCaseToWords = (str: string) => {
  return str
    .split('_')
    .map((word) => word[0].toUpperCase() + word.substr(1).toLowerCase())
    .join(' ');
};
